import React, { useEffect, useState } from "react";
import { api } from "../../config/api";
import { useMyContext } from "../../context/MyContext";
import { HiOutlinePencilSquare } from "react-icons/hi2";

const ytId = (url) => {
  try {
    const u = new URL(url);
    if (u.hostname === "youtu.be") return u.pathname.slice(1);
    if (u.hostname.includes("youtube.com")) {
      if (u.searchParams.get("v")) return u.searchParams.get("v");
      const parts = u.pathname.split("/").filter(Boolean);
      const idx = parts.indexOf("embed");
      if (idx !== -1 && parts[idx + 1]) return parts[idx + 1];
    }
  } catch {}
  return "";
};

export default function TutorialManager() {
  const { token } = useMyContext();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ url: "", title: "", description: "" });
  const TITLE_MAX = 120;
  const DESC_MAX = 300;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", description: "" });
  const [orderDirty, setOrderDirty] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/tutorials?all=true");
      setItems(data || []);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const authHeaders = token ? { headers: { authorization: `Bearer ${token}` } } : {};

  const add = async (e) => {
    e.preventDefault();
    setError("");
    if (!ytId(form.url)) { setError("Please enter a valid YouTube link"); return; }
    if (form.title && form.title.length > TITLE_MAX) { setError(`Title must be ${TITLE_MAX} characters or fewer`); return; }
    if (form.description && form.description.length > DESC_MAX) { setError(`Description must be ${DESC_MAX} characters or fewer`); return; }
    try {
      await api.post("/tutorials", form, authHeaders);
      setForm({ url: "", title: "", description: "" });
      await load();
      setCurrentPage(1);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to add");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this video?")) return;
    try {
      await api.delete(`/tutorials/${id}`, authHeaders);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to delete");
    }
  };

  // Reordering helpers operate on the full items array (not paginated slice)
  const moveItem = (globalIndex, direction) => {
    setItems(prev => {
      const arr = [...prev];
      const newIndex = globalIndex + direction;
      if (newIndex < 0 || newIndex >= arr.length) return prev;
      const [moved] = arr.splice(globalIndex, 1);
      arr.splice(newIndex, 0, moved);
      return arr;
    });
    setOrderDirty(true);
  };

  const persistOrder = async (arr) => {
    try {
      setSavingOrder(true);
      const orderedIds = arr.map(i => i._id);
      const resp = await api.post('/tutorials/reorder', { orderedIds }, authHeaders);
      if (resp?.data?.success) setOrderDirty(false);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to save order');
    } finally {
      setSavingOrder(false);
    }
  };

  const moveToIndex = async (fromIndex, toIndex) => {
    const len = items.length;
    if (fromIndex < 0 || fromIndex >= len) return;
    if (toIndex < 0) toIndex = 0;
    if (toIndex >= len) toIndex = len - 1;
    if (fromIndex === toIndex) return;
    const arr = [...items];
    const [moved] = arr.splice(fromIndex, 1);
    arr.splice(toIndex, 0, moved);
    setItems(arr);
    setOrderDirty(true);
    await persistOrder(arr);
    // Navigate to page where the item now resides
    const targetPage = Math.floor(toIndex / pageSize) + 1;
    setCurrentPage(targetPage);
  };

  const moveToTop = async (globalIndex) => moveToIndex(globalIndex, 0);
  const moveToBottom = async (globalIndex) => moveToIndex(globalIndex, items.length - 1);

  // Drag & Drop reorder within current list across pagination (uses global index)
  const handleDragStart = (index) => setDragIndex(index);
  const handleDragOver = (e, index) => {
    e.preventDefault(); // allow drop
    if (dragOverIndex !== index) setDragOverIndex(index);
  };
  const handleDrop = async (index) => {
    if (dragIndex === null || dragIndex === index) {
      setDragIndex(null); setDragOverIndex(null); return;
    }
    // Build next order from current state synchronously
    const arr = [...items];
    if (index < 0 || index > arr.length - 1 || dragIndex < 0 || dragIndex > arr.length - 1) {
      setDragIndex(null); setDragOverIndex(null); return;
    }
    const [moved] = arr.splice(dragIndex, 1);
    arr.splice(index, 0, moved);
    setItems(arr);
    setOrderDirty(true);

    try {
      setSavingOrder(true);
      const orderedIds = arr.map(i => i._id);
      const resp = await api.post('/tutorials/reorder', { orderedIds }, authHeaders);
      // If server responded OK, clear dirty and optionally sync from server
      if (resp?.data?.success) {
        setOrderDirty(false);
      }
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to save order');
    } finally {
      setSavingOrder(false);
      setDragIndex(null); setDragOverIndex(null);
    }
  };

  const saveOrder = async () => {
    try {
      setLoading(true);
      const orderedIds = items.map(i => i._id);
      await api.post('/tutorials/reorder', { orderedIds }, authHeaders);
      setOrderDirty(false);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to save order');
    } finally { setLoading(false); }
  };

  const resetOrderChanges = async () => {
    await load();
    setOrderDirty(false);
  };

  const toggle = async (id) => {
    setError("");
    try {
      // Preferred: atomic toggle
      await api.patch(`/tutorials/${id}/toggle`, null, authHeaders);
      await load();
    } catch (e) {
      // Fallback for servers without PATCH route: try explicit PUT flip
      try {
        const current = items.find(x => x._id === id);
        if (!current) throw e;
        await api.put(`/tutorials/${id}`, { isActive: !current.isActive }, authHeaders);
        await load();
      } catch (e2) {
        setError(e2?.response?.data?.error || "Failed to update");
      }
    }
  };

  const beginEdit = (item) => {
    setEditingId(item._id);
    setEditForm({ title: item.title || "", description: item.description || "" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ title: "", description: "" });
  };

  const saveEdit = async () => {
    setError("");
    if (editForm.title && editForm.title.length > TITLE_MAX) { setError(`Title must be ${TITLE_MAX} characters or fewer`); return; }
    if (editForm.description && editForm.description.length > DESC_MAX) { setError(`Description must be ${DESC_MAX} characters or fewer`); return; }
    try {
      await api.put(`/tutorials/${editingId}`, {
        title: editForm.title,
        description: editForm.description,
      }, authHeaders);
      await load();
      cancelEdit();
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to save changes");
    }
  };

  // Pagination derived values
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const start = (currentPage - 1) * pageSize;
  const pageItems = items.slice(start, start + pageSize);
  if (currentPage > totalPages && items.length) {
    // Clamp after deletions
    setTimeout(() => setCurrentPage(totalPages), 0);
  }

  return (
    <div className="bg-white rounded-md p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold">Tutorial Videos</h3>
        {(orderDirty || savingOrder) && (
          <div className="flex items-center gap-2">
            {orderDirty && <button onClick={resetOrderChanges} className="px-3 py-1 rounded border text-xs">Discard Order Changes</button>}
            {orderDirty && <button onClick={saveOrder} className="px-3 py-1 rounded text-xs bg-blue-600 text-white">Save Order</button>}
            {savingOrder && <span className="text-xs text-gray-500">Saving…</span>}
          </div>
        )}
      </div>
      <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
        <input className="border rounded h-12 px-4 text-sm w-full" placeholder="YouTube URL" value={form.url} onChange={(e)=>setForm(f=>({...f,url:e.target.value}))} />
        <div className="flex flex-col">
          <input className="border rounded h-12 px-4 text-sm w-full" placeholder={`Title (optional, max ${TITLE_MAX})`} value={form.title} onChange={(e)=>{
            const v = e.target.value.slice(0, TITLE_MAX);
            setForm(f=>({...f,title:v}));
          }} />
          <div className="text-[11px] text-gray-500 mt-1">{(form.title?.length||0)}/{TITLE_MAX}</div>
        </div>
        <div className="flex flex-col">
          <input className="border rounded h-12 px-4 text-sm w-full" placeholder={`Description (optional, max ${DESC_MAX})`} value={form.description} onChange={(e)=>{
            const v = e.target.value.slice(0, DESC_MAX);
            setForm(f=>({...f,description:v}));
          }} />
          <div className="text-[11px] text-gray-500 mt-1">{(form.description?.length||0)}/{DESC_MAX}</div>
        </div>
        <div className="flex items-center md:items-start mt-0">
          <button type="submit" className="btnBg text-white rounded px-4 h-12 leading-none">Add Video</button>
        </div>
      </form>
      {/* Removed tips to save vertical space as requested */}
      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
      {loading ? (
        <p className="text-sm">Loading…</p>
      ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pageItems.map((v, idx) => {
            const globalIndex = start + idx;
            return (
            <div
              key={v._id}
              className={`relative border rounded-md overflow-hidden flex flex-col h-full ${dragOverIndex===globalIndex ? 'ring-2 ring-blue-400' : ''}`}
              draggable={editingId !== v._id}
              onDragStart={() => handleDragStart(globalIndex)}
              onDragOver={(e) => handleDragOver(e, globalIndex)}
              onDrop={() => handleDrop(globalIndex)}
              title="Drag to reorder"
            >
              {/* Top-right actions */}
              <div className="relative">
                <img src={`https://img.youtube.com/vi/${v.videoId}/hqdefault.jpg`} alt={v.title || 'thumb'} className="w-full h-40 object-cover" />
                {editingId !== v._id ? (
                  <button
                    onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); beginEdit(v); }}
                    onMouseDown={(e)=>{ e.stopPropagation(); }}
                    draggable={false}
                    className="absolute top-3 right-3 z-20 p-1.5 rounded-md bg-white/90 border border-gray-200 hover:bg-white shadow text-[#053F92]"
                    aria-label="Edit video"
                    title="Edit title & description"
                  >
                    <HiOutlinePencilSquare size={18} />
                  </button>
                ) : null}
              </div>
              {/* No absolute buttons here to avoid overlapping inputs */}
              <div className="p-3 flex flex-col h-full">
                {editingId === v._id ? (
                  <>
                    <div className="flex flex-col md:flex-row gap-3">
                      <div className="flex-1">
                        <input
                          className="w-full border rounded p-2 text-sm"
                          placeholder={`Title (max ${TITLE_MAX})`}
                          value={editForm.title}
                          onChange={(e)=> setEditForm(f=>({...f,title: e.target.value.slice(0, TITLE_MAX)}))}
                        />
                        <div className="text-[11px] text-gray-500 mt-1">{editForm.title.length}/{TITLE_MAX}</div>
                      </div>
                      <div className="flex-1">
                        <input
                          className="w-full border rounded p-2 text-sm"
                          placeholder={`Description (max ${DESC_MAX})`}
                          value={editForm.description}
                          onChange={(e)=> setEditForm(f=>({...f,description: e.target.value.slice(0, DESC_MAX)}))}
                        />
                        <div className="text-[11px] text-gray-500 mt-1">{editForm.description.length}/{DESC_MAX}</div>
                      </div>
                    </div>
                    {/* Inline toolbar for Update/Cancel below inputs */}
                    {/* Anchor actions to the bottom so there is no extra gap below */}
                    <div className="mt-auto pt-3 flex items-center justify-between">
                      <button
                        onClick={saveEdit}
                        className="h-9 px-4 rounded-md text-sm text-white btnBg shadow"
                        title="Update"
                      >
                        Update
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="h-9 px-4 rounded-md text-sm bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 shadow"
                        title="Cancel"
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="font-medium text-sm">{v.title || 'Tutorial'}</p>
                    {v.description && <p className="text-xs text-gray-500 mt-1 max-h-10 overflow-hidden leading-5">{v.description}</p>}
                    <div className="mt-auto pt-3 flex flex-wrap gap-2">
                      {/* Reorder shortcuts */}
                      <button
                        onClick={() => moveToTop(globalIndex)}
                        className="h-8 px-3 rounded-md text-xs border border-gray-300 text-gray-700 hover:bg-gray-50"
                        title="Move to top"
                      >Top</button>
                      <button
                        onClick={() => moveToBottom(globalIndex)}
                        className="h-8 px-3 rounded-md text-xs border border-gray-300 text-gray-700 hover:bg-gray-50"
                        title="Move to bottom"
                      >Bottom</button>
                      <button
                        onClick={() => toggle(v._id)}
                        className={`h-8 px-3 rounded-md text-xs shrink-0 whitespace-nowrap ${v.isActive ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-green-600 text-white hover:bg-green-700'}`}
                        aria-label={v.isActive ? 'Deactivate video' : 'Activate video'}
                        title={v.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {v.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button onClick={() => remove(v._id)} className="h-8 px-3 rounded-md text-xs bg-red-600 text-white hover:bg-red-700 shrink-0 whitespace-nowrap">Delete</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )})}
        </div>
        {/* Pagination */}
        {items.length > 0 && (
          <div className="flex items-center justify-center py-3 gap-3 select-none">
            <button
              aria-label="Previous page"
              disabled={currentPage===1}
              onClick={()=>setCurrentPage(p=>Math.max(1,p-1))}
              className={`h-8 w-8 grid place-items-center rounded-full border ${currentPage===1? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200':'bg-gray-100 text-gray-600 hover:bg-gray-200 border-gray-300'}`}
            >
              ‹
            </button>
            <div className="flex items-center gap-2">
              {Array.from({length: totalPages}).map((_,i)=>{
                const page=i+1;
                const shouldShow = totalPages<=7 || Math.abs(page-currentPage)<=1 || page===1 || page===totalPages;
                if(!shouldShow) return (i===1 || i===totalPages-2) ? <span key={i} className="text-gray-400 px-1">…</span> : null;
                const isActive = page===currentPage;
                return (
                  <button key={page} onClick={()=>setCurrentPage(page)}
                    className={isActive ? 'px-3 py-1 rounded-lg border border-blue-500 text-blue-600 font-medium shadow-[0_0_0_3px_rgba(59,130,246,0.15)]' : 'px-2.5 py-1 rounded-lg text-gray-700 hover:text-blue-600 border border-transparent hover:border-blue-300'}>
                    {page}
                  </button>
                );
              })}
            </div>
            <button
              aria-label="Next page"
              disabled={currentPage===totalPages}
              onClick={()=>setCurrentPage(p=>Math.min(totalPages,p+1))}
              className={`h-8 w-8 grid place-items-center rounded-full border ${currentPage===totalPages? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200':'bg-gray-100 text-gray-600 hover:bg-gray-200 border-gray-300'}`}
            >
              ›
            </button>
          </div>
        )}
        </>
      )}
    </div>
  );
}
